import SessionEventScope from '@/models/Metrics/SessionEventScope';
import SessionEventType from '@/models/Metrics/SessionEventType';
import { ref as vueRef } from 'vue';
import useAuthenticatedStudent from '@/composables/v2/metrics/useAuthenticatedStudent';

export interface SessionEvent {
    studentId?: number;
    scope: SessionEventScope;
    /* Real task_id (versioned/editable tasks) or a legacy task name string (PEM/END/old MAT-DIV-style
     * system tasks, and RUM transaction names) — the backend routes based on the actual type. */
    taskId?: string | number;
    stepId?: string;
    type: SessionEventType;
    ref?: string;
    value?: Record<string, unknown>;
    createdAt: Date | string; // Stored as string in the localStorage and as Date in memory
}

const previousEvent = vueRef<SessionEvent | null>(null);

export function createSessionEvent(
    scope: SessionEventScope,
    type: SessionEventType,
    taskId?: string | number,
    stepId?: string,
    ref?: string,
    value?: Record<string, unknown>,
    studentId?: number
): SessionEvent {
    const now = new Date();
    const { studentId: authenticatedStudentId } = useAuthenticatedStudent();

    const prevCreatedAt = previousEvent.value ? (previousEvent.value.createdAt as Date) : null;
    const isSameCreatedAt =
        prevCreatedAt &&
        now.getHours() === prevCreatedAt.getHours() &&
        now.getMinutes() === prevCreatedAt.getMinutes() &&
        now.getSeconds() === prevCreatedAt.getSeconds() &&
        now.getMilliseconds() === prevCreatedAt.getMilliseconds();

    if (isSameCreatedAt) {
        now.setMilliseconds(now.getMilliseconds() + 1);
    }

    const event: SessionEvent = {
        scope,
        type,
        taskId,
        stepId,
        ref,
        value,
        createdAt: now,
    };

    const finalStudentId = studentId ?? authenticatedStudentId.value;

    // The studentId is only set when it is a number
    if (typeof finalStudentId === 'number') {
        event.studentId = finalStudentId;
    }

    previousEvent.value = event;

    return event;
}
